import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { DEFAULT_DIALOG_NO_PADDING_CLASS, HelperUtils, PromptConfirmDialogComponent } from "@merri/common";
import { catchError, concatMap, Observable, of, Subject, switchMap, take, takeUntil, tap, throwError } from "rxjs";
import { SnackBarService } from "src/app/core/services/snack-bar.service";
import { HelperMethods } from "src/app/shared/helper-methods";
import { Partner } from "../models/partner";
import { User } from "../models/user";
import { UserData } from "../models/user-data";
import { LinkedPartnerUsersDialogConfig } from "../models/linked-partner-users-dialog-config";
import { LinkedPartnerUsersServiceBase } from "../services/linked-partner-users-service-base";

@Component({
    selector: "app-linked-partner-users-dialog",
    templateUrl: "./linked-partner-users-dialog.component.html",
    styleUrls: ["./linked-partner-users-dialog.component.scss"],
    standalone: false
})
export class LinkedPartnerUsersDialogComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();
  private _currentPartnerId: number;

  loading: boolean;
  loadingError: string;
  loadingUsers: boolean;
  loadingUsersError: string;
  partners: Partner[] = [];
  userData: UserData;
  submitting: boolean;
  submitError: string;
  submitSuccess: boolean;

  partnerForm: FormGroup = this._fb.group({
    partnerId: [],
    selectedUsers: [],
  });

  constructor(
    private _dialogRef: MatDialogRef<LinkedPartnerUsersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LinkedPartnerUsersDialogConfig,
    private _matDialog: MatDialog,
    private _linkedPartnerUsersService: LinkedPartnerUsersServiceBase,
    private _fb: FormBuilder,
    private _snackBarService: SnackBarService,
  ) {}

  get selectedUsersFormControl(): AbstractControl {
    return this.partnerForm.controls.selectedUsers;
  }

  ngOnInit(): void {
    this._dialogRef.addPanelClass(DEFAULT_DIALOG_NO_PADDING_CLASS);
    this._dialogRef.disableClose = true;

    this._dialogRef
      .keydownEvents()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: event => {
          if (event.key === "Escape") {
            this._close();
          }
        },
      });

    this._dialogRef
      .backdropClick()
      .pipe(takeUntil(this._destroyed$))
      .subscribe({ next: () => this._close() });

    this._refresh();

    this.partnerForm.controls.partnerId.valueChanges
      .pipe(
        switchMap((partnerId: number) => {
          let obs$: Observable<boolean>;
          if (!this.selectedUsersFormControl.pristine) {
            this.loadingUsers = true;
            obs$ = this._unsavedChangesPrompt();
          } else {
            obs$ = of(true);
          }

          return obs$.pipe(
            concatMap(result => {
              if (result) {
                this.selectedUsersFormControl.markAsPristine();
                this._currentPartnerId = partnerId;
                return this._refreshUsers$();
              } else {
                this.partnerForm.controls.partnerId.setValue(this._currentPartnerId, { emitEvent: false });
                this.loadingUsers = false;
                return of(null);
              }
            }),
          );
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.selectedUsersFormControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe({
      next: selectedUsers => {
        if (HelperUtils.arraysEqual(selectedUsers, this.userData.selectedUsers)) {
          this.selectedUsersFormControl.markAsPristine();
        } else if (this.selectedUsersFormControl.pristine) {
          this.selectedUsersFormControl.markAsDirty();
        }
      },
    });

    if (this.data.selectedPartnerId) {
      this.partnerForm.patchValue({
        partnerId: +this.data.selectedPartnerId,
      });
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onDiscardChanges(): void {
    PromptConfirmDialogComponent.promptYesConfirm$(this._matDialog, {
      title: "Discard Changes?",
      header: "Do you wish to continue?",
      message: "Discarding these changes will revert your changes.",
    })
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: result => {
          if (result) {
            this.selectedUsersFormControl.setValue(this.userData.selectedUsers);
          }
        },
      });
  }

  onSaveClick(closeAfterSave: boolean = false): void {
    PromptConfirmDialogComponent.promptYesConfirm$(this._matDialog, {
      title: "Ready to save?",
      header: "Do you wish to continue?",
      message: "Saving these changes will modify the access of these users.",
    })
      .pipe(
        concatMap(result => {
          if (result) {
            this.submitting = true;
            this.submitError = null;
            this.submitSuccess = false;

            this.partnerForm.disable({ emitEvent: false });
            return this._linkedPartnerUsersService
              .postPartnerUsers$(this.data.catalogEntityId, this.partnerForm.controls.partnerId.value, this.selectedUsersFormControl.value)
              .pipe(
                tap(() => {
                  if (closeAfterSave) {
                    this._close(true);
                    this._snackBarService.show({
                      message: "Changes saved successfully",
                    });
                    return;
                  }

                  this.userData.selectedUsers = this.selectedUsersFormControl.value;
                  this.selectedUsersFormControl.markAsPristine();

                  this.submitting = false;
                  this.partnerForm.enable({ emitEvent: false });

                  this.submitSuccess = true;

                  HelperMethods.delayRun(5000, () => {
                    this.submitSuccess = false;
                  });
                }),
                catchError(err => {
                  this.submitError = HelperUtils.getResponseErrors(err)[0];
                  this.submitting = false;
                  this.partnerForm.enable({ emitEvent: false });

                  return of(null);
                }),
              );
          }
          return of(null);
        }),
      )
      .subscribe();
  }

  usersSearch: (item: User, search: string) => boolean = (item, search) => {
    return item.fullName.toLowerCase().includes(search.toLowerCase()) || item.email.toLowerCase().includes(search.toLowerCase());
  };

  onCloseClick(): void {
    this._close();
  }

  onLoadingRefreshClick(): void {
    this._refresh();
  }

  onLoadingUsersRefreshClick(): void {
    this._refreshUsers$().pipe(take(1), takeUntil(this._destroyed$)).subscribe();
  }

  onRefreshUsersClick(): void {
    this._refreshUsers$().pipe(take(1), takeUntil(this._destroyed$)).subscribe();
  }

  private _refresh(): void {
    this.loading = true;
    this.loadingError = null;

    this._linkedPartnerUsersService
      .getCatalogEntityPartners$(this.data.catalogEntityId)
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: partners => {
          this.partners = partners;
          this.loading = false;
        },
        error: err => {
          this.loadingError = HelperUtils.getResponseErrors(err)[0];
          this.loading = false;
        },
      });
  }

  private _refreshUsers$(): Observable<UserData> {
    const partnerId = +this.partnerForm.controls.partnerId.value;

    this.loadingUsers = true;
    this.loadingUsersError = null;

    return this._linkedPartnerUsersService.getPartnerUsers$(this.data.catalogEntityId, partnerId).pipe(
      tap(userData => {
        this.userData = userData;
        this.selectedUsersFormControl.setValue(userData.selectedUsers);
        this.loadingUsers = false;
      }),
      catchError(err => {
        this.loadingUsersError = HelperUtils.getResponseErrors(err)[0];
        this.loadingUsers = false;
        return throwError(() => HelperUtils.getResponseErrors(err));
      }),
    );
  }

  private _close(forceClose: boolean = false): void {
    if (this.submitting && !forceClose) {
      return;
    }

    let obs$: Observable<boolean>;

    if (forceClose || this.selectedUsersFormControl.pristine) {
      obs$ = of(true);
    } else {
      obs$ = this._unsavedChangesPrompt();
    }

    obs$.pipe(take(1), takeUntil(this._destroyed$)).subscribe({
      next: result => {
        if (result) {
          this._dialogRef.close();
        }
      },
    });
  }

  private _unsavedChangesPrompt(): Observable<boolean> {
    return PromptConfirmDialogComponent.promptYesConfirm$(this._matDialog, {
      title: "Unsaved Changes",
      header: "Do you wish to continue?",
      message:
        "You have unsaved changes for this customer. Changes will be lost if you continue. Please save or discard changes before continuing.",
    });
  }
}
